import { useState } from "react";
import { Search, Package, BookOpen, Clock, User, Trash } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "@packages/hooks/useAnalytics";
import { TextLinear, Text } from "@core/text";
import {
  useGetTedLessonPlanners,
  useDeleteTedLessonPlanner,
} from "@packages/api/madlenAPI";
import { useToast } from "@packages/contexts/ToastContext";
import { CourseData } from "@packages/constant/CourseData";
import { format } from "date-fns";
import { tr, enUS } from "date-fns/locale";

const PastTedPlans = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const { trackEvent: track } = trackEvent();
  const { addToast } = useToast();

  const { data: tedPlans, isLoading, refetch } = useGetTedLessonPlanners();

  const deleteTedPlan = useDeleteTedLessonPlanner({
    mutation: {
      onSuccess: () => {
        addToast({
          title: t("tedPlan.delete.success"),
          variant: "success",
        });
        refetch();
        track({
          category: "ContentDelete",
          action: "ted.planner.delete",
          label: "TedPlanner delete success",
          nonInteraction: false,
        });
      },
      onError: (error) => {
        console.error("Error deleting TED plan:", error);
        addToast({
          title: t("tedPlan.delete.error"),
          variant: "error",
        });
      },
    },
  });

  const handleClickGenerate = () => {
    track(
      {
        category: "ContentGenerate",
        action: "ted.planner.generate.new",
        label: "TedPlanner generate new click",
        nonInteraction: false,
      },
      {
        location: "/lesson-planning-tools/ted-plan",
        element: "generate-button",
      },
    );
    navigate("/lesson-planning-tools/ted-plan/new");
  };

  const handleViewDetails = (planId: number) => {
    track(
      {
        category: "ContentViewDetail",
        action: "ted.planner.past.plans.view.detail",
        label: "TedPlannerPastPlans view details click",
        nonInteraction: false,
      },
      {
        location: "/ted-planner-past-plans/view-detail",
        element: "view-detail-button",
      },
    );
    navigate(`/lesson-planning-tools/ted-plan/${planId}`);
  };

  const handleDeletePlan = (planId: number, planName: string) => {
    if (window.confirm(t("tedPlan.delete.confirm", { name: planName }))) {
      deleteTedPlan.mutate({ id: planId });
    }
  };

  // Filter plans based on search and course selection
  const filteredPlans = tedPlans?.filter((plan) => {
    const matchesSearch = (plan.name || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesCourse = selectedCourse
      ? plan.courseName === selectedCourse
      : true;
    return matchesSearch && matchesCourse;
  });

  const showEmptyState =
    !isLoading && (!filteredPlans || filteredPlans.length === 0);

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
      {[...Array(6)].map((_, index) => (
        <div
          key={index}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 animate-pulse"
        >
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded mb-3 w-3/4"></div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded mb-6 w-1/2"></div>
          <div className="flex justify-between items-center">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
          </div>
        </div>
      ))}
    </div>
  );

  // Ted Plan Card component
  const TedPlanCard = ({ plan }: { plan: any }) => {
    const totalSteps = plan.steps?.length || 0;
    const totalDuration =
      plan.steps?.reduce(
        (sum: number, step: any) => sum + (step.sure_dakika || 0),
        0,
      ) || 0;

    return (
      <div className="group bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col h-full">
          {/* Header with gradient overlay */}
          <div className="relative bg-gradient-to-r from-blue-500 to-blue-600 p-4 pb-3">
            <div className="min-w-0 w-full mb-2">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-lg font-semibold text-white truncate flex-1">
                  {plan.name}
                </h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePlan(plan.id, plan.name);
                  }}
                  className="p-1 hover:bg-white/20 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                  title={t("common.delete")}
                >
                  <Trash
                    size={16}
                    className="text-white/80 hover:text-red-200"
                  />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4 text-blue-100">
              <div className="flex items-center gap-1 text-sm">
                <User className="w-4 h-4" />
                <span>
                  {t("common.grade")} {plan.grade}
                </span>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <BookOpen className="w-4 h-4" />
                <span>{t(`courseType.${plan.courseName}`)}</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>
                    {totalDuration} {t("common.time.minutes")}
                  </span>
                </div>
                <span>
                  {totalSteps} {t("common.steps")}
                </span>
              </div>

              {plan.createdAt && !isNaN(new Date(plan.createdAt).getTime()) && (
                <div className="text-xs text-gray-500 dark:text-gray-500">
                  {format(new Date(plan.createdAt), "dd MMM yyyy", {
                    locale: i18n.language === "tr" ? tr : enUS,
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="p-4 pt-0">
            <button
              onClick={() => handleViewDetails(plan.id)}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium"
            >
              {t("common.view")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-900">
      <div className="container max-w-7xl mx-auto px-4 py-6">
        <div className="mb-8 text-center space-y-4">
          <div className="max-w-4xl mx-auto">
            <TextLinear
              variant={"heroTitle"}
              className="leading-tight"
              from="from-blue-600 dark:from-blue-400"
              to="to-blue-800 dark:to-blue-300"
            >
              Ted Plan
            </TextLinear>
          </div>
          <Text
            variant="p"
            status="secondary"
            size="base"
            className="max-w-2xl mx-auto"
          >
            Manage and create your Ted lesson plans with step-by-step guidance
          </Text>
          <div className="pt-2">
            <button
              onClick={handleClickGenerate}
              className="btn bg-gradient-to-r from-blue-600 to-blue-800 hover:opacity-90 text-white px-8 py-3 text-base rounded-xl shadow-lg hover:shadow-xl transform transition-all duration-200 hover:-translate-y-1 font-medium"
            >
              {t("common.create")}
            </button>
          </div>
        </div>

        {/* Search and Filter Section */}
        {!showEmptyState && (
          <div className="mb-6 bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder={t("common.search")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>
              <div className="md:w-48">
                <select
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">{t("common.all")}</option>
                  {Object.entries(CourseData).map(([key, course]) => (
                    <option key={key} value={key}>
                      {t(`courseType.${(course as any).name(5)}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && <LoadingSkeleton />}

        {/* Plans Grid */}
        {!isLoading && !showEmptyState && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {filteredPlans?.map((plan) => (
              <TedPlanCard key={plan.id} plan={plan} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {showEmptyState && (
          <div className="flex flex-col items-center justify-center min-h-[500px] space-y-8 bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-sm p-16 border border-gray-100 dark:border-gray-700">
            <div className="p-8 rounded-full bg-blue-50 dark:bg-blue-900/30 ring-8 ring-blue-50/50 dark:ring-blue-900/20">
              <Package size={64} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div className="text-center space-y-4 max-w-lg">
              <h3 className="text-3xl font-semibold text-gray-900 dark:text-white">
                {searchTerm || selectedCourse
                  ? t("common.noResults.title")
                  : "No Ted Plans Yet"}
              </h3>
              <Text variant="p" status="subtle" size="lg">
                {searchTerm || selectedCourse
                  ? t("common.noResults.description")
                  : "Get started by creating your first Ted Plan using the button above."}
              </Text>
            </div>
            {(searchTerm || selectedCourse) && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setSelectedCourse("");
                }}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                {t("common.clearFilters")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PastTedPlans;
